// src/controls/ProductControl.js
import Control from "ol/control/Control";
import {
  getInitialProductState,
  getRevisionControls,
  setProductState,
} from "../state/URLStateManager";

/**
 * "Orbis product" drawer. Lets the user pick a product + version; on select it
 * builds a layerId->revisionId map via the service and fans it out to every
 * registered RevisionControl. Placed by config/ProductAndControl.js (the main viewer's Data section).
 */
export class ProductControl extends Control {
  /** @param {{ service: object }} options */
  constructor(options = {}) {
    const details = document.createElement("details");
    details.className = "product-drawer";
    details.open = false;
    super({ element: details, target: undefined });

    this._service = options.service;
    this._map = null; // current layerId->rev map

    const summary = document.createElement("summary");
    summary.textContent = "Orbis product";
    details.appendChild(summary);

    const body = document.createElement("div");
    body.className = "product-body";
    details.appendChild(body);

    this._summaryEl = summary;
    // Mirrored as a property so the main viewer's Data row can show it on its own
    // toggle button (the native summary is hidden there).
    this.set("summaryText", summary.textContent);
    const selects = document.createElement("div");
    selects.className = "product-selects";
    body.appendChild(selects);
    this._productSel = this._buildSelect(selects, "Pick a product");
    this._versionSel = this._buildSelect(selects, "Pick a report");
    this._versionSel.disabled = true;

    this._status = document.createElement("div");
    this._status.className = "product-status";
    body.appendChild(this._status);

    this._undoBar = document.createElement("div");
    this._undoBar.className = "product-undo";
    this._undoBar.setAttribute("role", "status");
    this._undoBar.hidden = true;
    body.appendChild(this._undoBar);

    this._bulk = document.createElement("div");
    this._bulk.className = "product-bulk";
    this._bulk.hidden = true;
    body.appendChild(this._bulk);
    this._buildBulk();

    // Refresh index when the product dropdown is opened/focused.
    this._productSel.addEventListener("mousedown", () => this._loadIndex());
    this._productSel.addEventListener("focus", () => this._loadIndex());
    this._productSel.addEventListener("change", () => this._onProductChange());
    this._versionSel.addEventListener("change", () => this._onVersionChange());

    this._loadIndex();
    this._restoreFromUrl();
  }

  _setSummary(text) {
    this._summaryEl.textContent = text;
    this.set("summaryText", text);
  }

  async _restoreFromUrl() {
    const initial = getInitialProductState();
    if (!initial) return;
    // Reload WITH a product in the URL -> open the drawer.
    this.element.open = true;
    await this._loadIndex();
    this._productSel.value = initial.scope;
    this._onProductChange();
    // version option values are reportPaths; find the one whose name matches
    const versions = this._service.listVersions(initial.scope);
    const match = versions.find((v) => v.name === initial.version);
    if (match) {
      this._versionSel.value = match.reportPath;
      // Restore: derive labels from the URL revision values, never overwrite them.
      await this._onVersionChange(true);
    }
  }

  _buildSelect(parent, labelText) {
    const span = document.createElement("span");
    const label = document.createElement("label");
    label.textContent = labelText;
    const sel = document.createElement("select");
    label.htmlFor = sel.id = `product-sel-${labelText.replace(/\s+/g, "-")}`;
    span.appendChild(label);
    span.appendChild(sel);
    parent.appendChild(span);
    return sel;
  }

  /** Coalesce concurrent calls onto one cached in-flight promise. */
  _loadIndex() {
    if (this._indexPromise) return this._indexPromise;
    this._indexPromise = (async () => {
      const prev = this._productSel.value;
      // Safari's native <select> popup snapshots its options when it opens and
      // ignores later async option changes — so the refresh-on-open must not
      // wipe an already-populated dropdown to "Loading…", nor replace its
      // options when nothing changed, or it stays stuck on the stale snapshot.
      // Show the placeholder only on the first (empty) load; else refresh quietly.
      const hasProducts = this._productSel.options.length > 1;
      if (!hasProducts) {
        this._setOptions(this._productSel, [
          { value: "", label: "Loading products…" },
        ]);
      }
      try {
        await this._service.refreshIndex();
        const products = this._service.listProducts();
        const opts = [{ value: "", label: "— Select a product —" }].concat(
          products.map((p) => ({ value: p.code, label: p.displayName })),
        );
        const current = Array.from(this._productSel.options).map(
          (o) => o.value,
        );
        const changed =
          current.length !== opts.length ||
          opts.some((o, i) => o.value !== current[i]);
        if (changed) {
          this._setOptions(this._productSel, opts);
          if (prev) this._productSel.value = prev;
        }
        this._status.textContent = "";
      } catch (e) {
        this._setOptions(this._productSel, [{ value: "", label: "—" }]);
        this._status.textContent = `Couldn't load products — ${e.message}`;
      }
    })().finally(() => {
      this._indexPromise = null;
    });
    return this._indexPromise;
  }

  _onProductChange() {
    const scope = this._productSel.value;
    if (!scope) {
      this._versionSel.disabled = true;
      this._setOptions(this._versionSel, [
        { value: "", label: "Select a product first" },
      ]);
      return;
    }
    const versions = this._service.listVersions(scope);
    const opts = [{ value: "", label: "— Select a version —" }].concat(
      versions.map((v) => ({
        value: v.reportPath,
        label: v.name,
      })),
    );
    this._setOptions(this._versionSel, opts);
    this._versionSel.disabled = false;
  }

  /**
   * @param {boolean} fromUrl URL restore: derive labels only, do not overwrite
   *   restored revision values (passed down to each RevisionControl).
   */
  async _onVersionChange(fromUrl = false) {
    const reportPath = this._versionSel.value;
    const scope = this._productSel.value;
    if (!reportPath) return;
    this._status.textContent = "Loading version…";
    try {
      this._map = await this._service.getProductMap(reportPath);
      const label =
        this._productSel.options[this._productSel.selectedIndex].text;
      const match = this._service
        .listVersions(scope)
        .find((v) => v.reportPath === reportPath);
      const versionName = match ? match.name : reportPath;
      this._appliedVersionName = versionName; // used in the undo details header
      this._applyToAll(label, fromUrl);
      this._setSummary(`Orbis product — ${label} · ${versionName}`);
      setProductState(scope, versionName);
      this._bulk.hidden = false;
      this._status.textContent = "";
    } catch (e) {
      this._status.textContent = `Couldn't load version — ${e.message}`;
    }
  }

  /** Fan the map out to all RevisionControls; show Undo for rows actually flipped. */
  _applyToAll(productLabel, fromUrl = false) {
    let rows = [];
    for (const { control } of getRevisionControls()) {
      control.setProductMap(this._map, productLabel, fromUrl);
      rows = rows.concat(control.getProductRows());
    }
    // Dedup by layerId — shared parent layers (e.g. 6, 29351) appear in many
    // per-layer pickers; collapse to one row and count each layer once.
    const byLayer = new Map();
    for (const r of rows) byLayer.set(r.layerId, r.revision);
    const deduped = [...byLayer]
      .map(([layerId, revision]) => ({ layerId, revision }))
      .sort((a, b) => a.layerId - b.layerId);
    this._showUndo(deduped.length, deduped, this._appliedVersionName);
  }

  _showUndo(changed, rows = [], versionName = "") {
    if (!changed) {
      this._undoBar.hidden = true;
      return;
    }
    this._undoBar.innerHTML = "";
    this._undoBar.append(`${changed} layer(s) switched to product revisions. `);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = "Undo";
    btn.addEventListener("click", () => this._doUndo());
    this._undoBar.appendChild(btn);
    if (rows.length)
      this._undoBar.appendChild(this._buildUndoDetails(rows, versionName));
    this._undoBar.hidden = false;
  }

  /** Inline <details> listing the switched layers as `layerId:revisionId`. */
  _buildUndoDetails(rows, versionName) {
    const lines = rows.map((r) => `${r.layerId}:${r.revision}`);

    const details = document.createElement("details");
    details.className = "product-undo-details";
    const summary = document.createElement("summary");
    summary.textContent = "Show details";
    details.appendChild(summary);

    const head = document.createElement("div");
    head.className = "product-undo-details-head";
    const scope = this._productSel.value;
    const label = scope
      ? this._productSel.options[this._productSel.selectedIndex].text
      : "product";
    const caption = document.createElement("span");
    caption.className = "product-undo-details-caption";
    caption.textContent = versionName ? `${label} · ${versionName}` : label;
    const copyBtn = document.createElement("button");
    copyBtn.type = "button";
    copyBtn.textContent = "Copy";
    copyBtn.addEventListener("click", () => {
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(lines.join("\n")).then(() => {
        copyBtn.textContent = "Copied";
        setTimeout(() => (copyBtn.textContent = "Copy"), 1200);
      });
    });
    head.appendChild(caption);
    head.appendChild(copyBtn);
    details.appendChild(head);

    const list = document.createElement("div");
    list.className = "product-undo-details-list";
    for (const line of lines) {
      const row = document.createElement("div");
      row.textContent = line;
      list.appendChild(row);
    }
    details.appendChild(list);
    return details;
  }

  _doUndo() {
    // Revert only the applied report: layers back to latest, keep the product
    // selected and its version list, reset only the version dropdown.
    for (const { control } of getRevisionControls()) control.clearProduct();
    this._undoBar.hidden = true;
    this._map = null;
    this._bulk.hidden = true;
    this._versionSel.value = "";
    const scope = this._productSel.value;
    const label = scope
      ? this._productSel.options[this._productSel.selectedIndex].text
      : null;
    this._setSummary(label ? `Orbis product — ${label}` : "Orbis product");
    setProductState(scope || null, null);
  }

  _buildBulk() {
    const toProduct = document.createElement("button");
    toProduct.type = "button";
    toProduct.textContent = "All to product";
    toProduct.addEventListener("click", () => {
      for (const { control } of getRevisionControls())
        control.bulkSetSource("product");
    });
    const toLatest = document.createElement("button");
    toLatest.type = "button";
    toLatest.textContent = "All to latest";
    toLatest.addEventListener("click", () => {
      for (const { control } of getRevisionControls())
        control.bulkSetSource("latest");
    });
    this._bulk.appendChild(toProduct);
    this._bulk.appendChild(toLatest);
  }

  _setOptions(sel, opts) {
    sel.innerHTML = "";
    for (const o of opts) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.textContent = o.label;
      sel.appendChild(opt);
    }
  }
}
